import { useEffect, useMemo, useState } from 'react';
import { fetchPublicProfessionals } from '../database/databaseProfiles';

const initials = (name = '') =>
  name
    .split(' ')
    .filter(Boolean)
    .map((w) => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

const coverImageFor = (person) => person.avatar_url || null;

export const parseSpecialties = (specialty = '') =>
  specialty
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);

const Avatar = ({ person, className, radiusClassName = 'rounded-tl-[4px] rounded-tr-[24px]', textClassName = 'text-3xl' }) => {
  const src = coverImageFor(person);
  return src ? (
    <img src={src} alt={person.full_name} className={`${className} ${radiusClassName}`} />
  ) : (
    <div
      className={`${className} ${radiusClassName} flex items-center justify-center bg-gradient-to-br from-terracotta-light to-terracotta text-cream font-serif ${textClassName}`}
    >
      {initials(person.full_name)}
    </div>
  );
};

const ContactButtons = ({ person }) => {
  const hasContact = person.phone || person.email;
  if (!hasContact) {
    return <p className="text-sm text-ink/60">Contacto próximamente</p>;
  }
  return (
    <div className="flex flex-col gap-2 mt-1">
      {person.phone && (
        <a
          href={`https://wa.me/${person.phone.replace(/\D/g, '')}`}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          className="text-center text-[11px] uppercase tracking-[0.2em] px-3 py-2.5 rounded-full bg-terracotta text-cream hover:bg-terracotta-dark transition"
        >
          WhatsApp
        </a>
      )}
      {person.email && <p className="text-center text-sm text-ink/70">Correo: {person.email}</p>}
    </div>
  );
};

const Profesionales = ({ activeFilter: controlledFilter, onFilterChange }) => {
  const [people, setPeople] = useState([]);
  const [loading, setLoading] = useState(true);
  const [internalFilter, setInternalFilter] = useState('Todos');
  const [selected, setSelected] = useState(null);

  const activeFilter = controlledFilter ?? internalFilter;
  const setActiveFilter = onFilterChange ?? setInternalFilter;

  useEffect(() => {
    (async () => {
      const data = await fetchPublicProfessionals();
      setPeople(data || []);
      setLoading(false);
    })();
  }, []);

  const categories = useMemo(() => {
    const unique = Array.from(new Set(people.flatMap((p) => parseSpecialties(p.specialty))));
    return ['Todos', ...unique];
  }, [people]);

  const filtered = useMemo(() => {
    if (activeFilter === 'Todos') return people;
    return people.filter((p) => parseSpecialties(p.specialty).includes(activeFilter));
  }, [people, activeFilter]);

  if (!loading && people.length === 0) return null;

  return (
    <section className="relative bg-cream pt-24 pb-20 px-6 overflow-hidden" id="profesionales">
      <div className="max-w-6xl mx-auto text-center mb-4">
        <p className="text-sm sm:text-lg md:text-2xl text-terracotta font-medium whitespace-nowrap">
          Servicios para que te sientas mejor, por dentro y por fuera.
        </p>
      </div>

      {categories.length > 1 && (
        <div className="flex flex-wrap justify-center gap-3 mb-10 max-w-5xl mx-auto">
          {categories.map((c) => (
            <button
              key={c}
              onClick={() => setActiveFilter(c)}
              className={`px-5 py-2 rounded-full text-sm border transition ${
                activeFilter === c
                  ? 'bg-terracotta border-terracotta text-cream'
                  : 'border-terracotta text-terracotta hover:bg-terracotta hover:text-cream'
              }`}
            >
              {c}
            </button>
          ))}
        </div>
      )}

      <div className="max-w-6xl mx-auto grid grid-cols-[repeat(auto-fill,minmax(270px,1fr))] gap-7">
        {filtered.map((p) => (
          <div
            key={p.id}
            onClick={() => setSelected(p)}
            className="cursor-pointer overflow-hidden bg-card border border-sand rounded-[4px_60px_4px_60px] p-7 flex flex-col gap-3 transition hover:-translate-y-1 hover:shadow-lg"
          >
            <Avatar
              person={p}
              className="max-w-none w-[calc(100%+3.5rem)] -mx-7 -mt-7 h-[210px] object-cover object-center block"
            />
            <div>
              <h3 className="text-xl font-sans font-medium text-ink leading-tight">{p.full_name}</h3>
              {p.specialty && <p className="text-[11px] uppercase tracking-[0.2em] text-terracotta">{p.specialty}</p>}
            </div>
            {p.bio && <p className="text-sm text-ink/80">{p.bio}</p>}
            <div className="flex flex-wrap gap-2">
              {p.services.map((s) => (
                <span key={s.id} className="text-xs px-3 py-1 rounded-full bg-sand text-terracotta-dark">
                  {s.title}
                </span>
              ))}
            </div>
            <ContactButtons person={p} />
          </div>
        ))}
      </div>

      {/* Modal */}
      {selected && (
        <div
          className="fixed inset-0 bg-ink/60 flex items-center justify-center z-50 p-4"
          onClick={() => setSelected(null)}
        >
          <div
            className="bg-cream rounded-[4px_70px_4px_70px] max-w-xl w-full max-h-[90vh] overflow-y-auto overflow-x-hidden p-8 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setSelected(null)}
              aria-label="Cerrar"
              className="absolute right-5 top-4 text-2xl text-cream drop-shadow z-10"
            >
              ×
            </button>

            <Avatar
              person={selected}
              className="max-w-none w-[calc(100%+4rem)] -mx-8 -mt-8 h-[260px] object-cover block mb-5"
              radiusClassName="rounded-tl-[4px] rounded-tr-[28px]"
              textClassName="text-4xl"
            />

            <h2 className="text-2xl font-sans font-medium text-ink">{selected.full_name}</h2>
            {selected.specialty && (
              <p className="text-[11px] uppercase tracking-[0.2em] text-terracotta mb-3">{selected.specialty}</p>
            )}
            {selected.bio && <p className="text-ink/80 mb-4">{selected.bio}</p>}

            <div className="divide-y divide-sand">
              {selected.services.map((s) => (
                <div key={s.id} className="flex items-center justify-between gap-4 py-3 text-sm">
                  <div className="min-w-0">
                    <div className="truncate">{s.title}</div>
                    {s.duration && <div className="text-xs text-ink/60">{s.duration}</div>}
                  </div>
                  <b className="text-terracotta whitespace-nowrap">
                    {s.price != null ? `€${s.price}` : 'Consultar precio'}
                  </b>
                </div>
              ))}
            </div>

            <div className="mt-6">
              <ContactButtons person={selected} />
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default Profesionales;
